import { useState, useEffect } from "react";
import { Play } from "lucide-react";
import { supabase } from "@/lib/supabase";

interface Props {
  videoUrl: string | null;
  thumbnailUrl: string | null;
  duration: number | null;
}

function formatDuration(s: number): string {
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s) % 60).padStart(2, "0")}`;
}

export default function ShortiePlayer({ videoUrl, thumbnailUrl, duration }: Props) {
  const [signedThumbUrl, setSignedThumbUrl] = useState<string | null>(null);
  const [signedVideoUrl, setSignedVideoUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoadingVideo, setIsLoadingVideo] = useState(false);

  // Generate signed thumbnail URL on mount
  useEffect(() => {
    if (!thumbnailUrl) return;
    supabase.storage
      .from("memory-images")
      .createSignedUrl(thumbnailUrl, 3600)
      .then(({ data }) => {
        if (data?.signedUrl) setSignedThumbUrl(data.signedUrl);
      });
  }, [thumbnailUrl]);

  const handlePlay = async () => {
    if (!videoUrl) return;
    setIsLoadingVideo(true);
    const { data } = await supabase.storage
      .from("videos")
      .createSignedUrl(videoUrl, 3600);
    if (data?.signedUrl) {
      setSignedVideoUrl(data.signedUrl);
      setIsPlaying(true);
    }
    setIsLoadingVideo(false);
  };

  // Playing state — video element
  if (isPlaying && signedVideoUrl) {
    return (
      <video
        src={signedVideoUrl}
        autoPlay
        controls
        playsInline
        preload="none"
        className="w-full rounded-xl"
      />
    );
  }

  // Thumbnail state — tap to play
  return (
    <div
      className="relative w-full cursor-pointer overflow-hidden rounded-xl bg-gray-100"
      onClick={handlePlay}
      style={{ minHeight: 200 }}
    >
      {signedThumbUrl ? (
        <img
          src={signedThumbUrl}
          alt="Video thumbnail"
          className="w-full object-cover"
          style={{ minHeight: 200 }}
        />
      ) : (
        <div className="flex items-center justify-center w-full" style={{ minHeight: 200 }}>
          <span className="text-3xl">🎬</span>
        </div>
      )}

      {/* Play button overlay */}
      <div className="absolute inset-0 flex items-center justify-center">
        {isLoadingVideo ? (
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-black/50">
            <span className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
          </div>
        ) : (
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-black/50 hover:bg-black/60 transition-colors">
            <Play className="h-6 w-6 text-white ml-0.5" />
          </div>
        )}
      </div>

      {/* Duration badge */}
      {duration != null && (
        <span className="absolute bottom-2 right-2 bg-black/60 text-white text-xs font-mono px-2 py-0.5 rounded">
          {formatDuration(duration)}
        </span>
      )}
    </div>
  );
}
